import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';
import Series from './series';
import ChipView from './chipView';
import ChipEdit from 'ChipEdit';
import chipDataTransform from 'chipDataTransform';
import VideoPlayer from './videoPlayer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const Layout = ({ content }) => {
    // 根据实际有数据的系列数组来决定是否启用滑动
    const seriesList = [content.series3, content.series2, content.series1].filter((item) => Array.isArray(item) && item.length > 0);
    const hasMultipleSeries = seriesList.length > 1;

    // Swiper 实例引用
    const swiperRef = useRef(null);
    // 记录当前索引和上一次的索引（用于判断切换方向、禁用按钮）
    const [currentIndex, setCurrentIndex] = useState(0);
    const prevActiveIndexRef = useRef(0);

    // 播放器相关状态
    const [hasFixVideo, setHasFixVideo] = useState(false);
    const [videoData, setVideoData] = useState(null);

    const closeHandle = () => {
        window.videoOrderList = undefined;
        setHasFixVideo(false);
        setVideoData(null);
    };

    const openVideoHandle = (vData) => {
        setHasFixVideo(true);
        setVideoData(vData);
    };

    return (
        <div className={styles.layout}>
            {hasMultipleSeries ? (
                <>
                    <div className={`${styles.leftPrev} prev1 ${currentIndex === 0 ? styles.prevDisabled : ''}`} />
                    <div className={styles.seriesSwiper}>
                        <Swiper
                            onSwiper={(swiper) => {
                                swiperRef.current = swiper;
                                prevActiveIndexRef.current = swiper.activeIndex;
                                setCurrentIndex(swiper.activeIndex);
                                // 禁用 Swiper 的自动 transform，让所有 slide 重叠在同一位置
                                const wrapper = swiper.wrapperEl;
                                wrapper.style.transform = 'translate3d(0px, 0, 0)';
                                wrapper.style.transition = 'none';

                                // 初始化所有 slide 的位置
                                setTimeout(() => {
                                    swiper.slides.forEach((slide, index) => {
                                        const slideEl = slide;
                                        if (index === swiper.activeIndex) {
                                            // 当前激活的 slide 在左侧
                                            slideEl.style.left = '0';
                                            slideEl.style.zIndex = '10';
                                            slideEl.style.opacity = '1';
                                        } else if (index < swiper.activeIndex) {
                                            // 所有在当前的之前的 slide 都在左侧（已显示过）
                                            slideEl.style.left = '0';
                                            slideEl.style.zIndex = '1';
                                            slideEl.style.opacity = '1';
                                        } else {
                                            // 所有在当前的之后的 slide 都在右侧等待
                                            slideEl.style.left = '100%';
                                            slideEl.style.zIndex = '1';
                                            slideEl.style.opacity = '1';
                                        }
                                    });
                                }, 50);
                            }}
                            onSlideChange={(swiper) => {
                                // 每次切换时保持 wrapper 不动，并更新 slide 位置
                                const wrapper = swiper.wrapperEl;
                                wrapper.style.transform = 'translate3d(0px, 0, 0)';
                                wrapper.style.transition = 'none';

                                // 判断切换方向：true 为向前（下一个），false 为向后（上一个）
                                const isForward = swiper.activeIndex > prevActiveIndexRef.current;
                                const prevIndex = prevActiveIndexRef.current;
                                const nextIndex = swiper.activeIndex;
                                prevActiveIndexRef.current = nextIndex;
                                setCurrentIndex(nextIndex);

                                // 更新 slide 位置
                                swiper.slides.forEach((slide, index) => {
                                    const slideEl = slide;
                                    // 确保 transition 样式一直存在
                                    slideEl.style.transition = 'left 0.7s ease-in-out';

                                    if (index === nextIndex) {
                                        // 当前激活的 slide
                                        if (isForward) {
                                            // 向前切换（点击"下一个"）：从右侧滑入覆盖
                                            slideEl.style.left = '100%';
                                            slideEl.style.zIndex = '10';
                                            slideEl.style.opacity = '1';
                                            // 使用双重 requestAnimationFrame 确保样式更新后再触发动画
                                            requestAnimationFrame(() => {
                                                requestAnimationFrame(() => {
                                                    slideEl.style.left = '0';
                                                });
                                            });
                                        } else {
                                            // 向后切换（点击"上一个"）：已经在左侧，但保持低z-index让滑出的slide可见
                                            slideEl.style.left = '0';
                                            slideEl.style.opacity = '1';
                                            slideEl.style.zIndex = '1'; // 先保持低z-index
                                            // 等滑出动画完成后再提高z-index（动画时长700ms + 缓冲50ms）
                                            setTimeout(() => {
                                                slideEl.style.zIndex = '10';
                                            }, 750);
                                        }
                                    } else if (isForward && index === prevIndex) {
                                        // 向前切换时：上一个 slide 保持在左侧但被覆盖（z-index 降低）
                                        slideEl.style.left = '0';
                                        slideEl.style.zIndex = '1';
                                        slideEl.style.opacity = '1';
                                    } else if (!isForward && index === prevIndex) {
                                        // 向后切换时：之前显示的 slide 向右滑出（left: 0 → 100%）
                                        // 这是关键：保持最高z-index让滑出动画完全可见
                                        slideEl.style.left = '0';
                                        slideEl.style.zIndex = '20'; // 最高z-index，确保滑出动画可见
                                        slideEl.style.opacity = '1';
                                        // 使用双重 requestAnimationFrame 确保样式更新后再触发滑出动画
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                slideEl.style.left = '100%';
                                                // 滑出动画完成后（700ms + 缓冲50ms）降低z-index
                                                setTimeout(() => {
                                                    slideEl.style.zIndex = '1';
                                                }, 750);
                                            });
                                        });
                                    } else if (index < nextIndex) {
                                        // 所有在当前 slide 之前的 slide 都保持在左侧（已显示过）
                                        slideEl.style.left = '0';
                                        slideEl.style.zIndex = '1';
                                        slideEl.style.opacity = '1';
                                    } else {
                                        // 所有在当前 slide 之后的 slide 都在右侧等待
                                        slideEl.style.left = '100%';
                                        slideEl.style.zIndex = '1';
                                        slideEl.style.opacity = '1';
                                    }
                                });
                            }}
                            className={styles.swiper}
                            direction="horizontal"
                            allowTouchMove={false}
                            slidesPerView={1}
                            spaceBetween={0}
                            speed={700}
                            watchSlidesProgress={true}
                            watchSlidesVisibility={true}
                            modules={[Navigation]}
                            navigation={{
                                prevEl: '.prev1',
                                nextEl: '.next1',
                            }}>
                            {seriesList.map((series, index) => (
                                <SwiperSlide className={styles.swiperSlide} key={index}>
                                    <Series content={series} onVideoPlay={openVideoHandle} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                    <div className={`${styles.rightPrev} next1 ${currentIndex === seriesList.length - 1 ? styles.nextDisabled : ''}`} />
                </>
            ) : (
                <>
                    <Series content={seriesList[0] || content.series1} onVideoPlay={openVideoHandle} />
                </>
            )}
            <VideoPlayer videoData={videoData} visible={hasFixVideo} onClose={closeHandle} />
            <ChipView />
            <ChipEdit transform={chipDataTransform} />
            <div id="ifeng_share_thumbnail" style={{ display: 'none' }}>
                {content?.shareInfo?.thumbnail || 'https://p1.ifengimg.com/29b92e35b2b20708/2017/51/ifengLogo300x300.jpg'}
            </div>
            <div id="ifeng_share_title" style={{ display: 'none' }}>
                {content?.shareInfo?.title || ''}
            </div>
            <div id="ifeng_share_description" style={{ display: 'none' }}>
                {content?.shareInfo?.desc || ''}
            </div>
            <div id="ifeng_share_url" style={{ display: 'none' }}>
                {content?.shareInfo?.url || ''}
            </div>
        </div>
    );
};

Layout.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Layout);
